import React from 'react';
import queryString from 'query-string';
import styled from 'styled-components/macro';
import {
  Input,
  Button,
  Checkbox,
  Typography,
  TextLink,
} from '@deeptrue-frontend/ui';
import { Formik, Field as FormikField, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import ErrorMessages from 'components/Fields/ErrorMessages';

export interface FormValues {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  passwordRepeat: string;
  name?: any;
  terms_agreement: boolean;
  non_field_errors?: any;
  privacy_policy_accepted: boolean;
}

export type SubmitHandler = (
  values: FormValues,
  helpers: FormikHelpers<FormValues>
) => void;

const CheckboxWrapper = styled.div`
  display: flex;
  align: flex-start;
  margin-top: 1rem;
`;

const TwoFieldsWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  > * {
    flex: 0 1 48%;
  }
`;

const StyledForm = styled.form`
  width: 100%;
  margin-top: 1rem;
`;

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    email: Yup.string()
      .email(t('errors.fields.email.format', 'Invalid email format'))
      .required(t('errors.fields.required', 'Field is required')),
    password: Yup.string()
      .required(t('errors.fields.required', 'Field is required'))
      .min(
        8,
        t(
          'fields.errors.min8chars',
          'Password should have at least 8 characters'
        )
      ),
    terms_agreement: Yup.boolean().oneOf(
      [true],
      t('errors.fields.required', 'Field is required')
    ),
    privacy_policy_accepted: Yup.boolean().oneOf(
      [true],
      t('errors.fields.required', 'Field is required')
    ),
    firstName: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    lastName: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    passwordRepeat: Yup.string()
      .oneOf(
        [Yup.ref('password'), null],
        t('errors.fields.password.match', 'Passwords do not match')
      )
      .required(t('errors.fields.required', 'Field is required')),
    name: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
  });

interface Props {
  onSubmit: SubmitHandler;
  initialValues: FormValues;
  organization_name?: string;
  location?: any;
}
const SignUpForm = ({ onSubmit, initialValues, location }: Props) => {
  const t = useFormatMessage();
  const defaultLanguage = 'en';
  const parsedSearch = queryString.parse(location.search);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={getValidationSchema(t)}
    >
      {({
        handleSubmit,
        errors,

        touched,
        isSubmitting,
        values,
        setFieldValue,
      }) => {
        const nonFieldErrors = errors.non_field_errors;
        return (
          <StyledForm onSubmit={handleSubmit}>
            <FormikField
              name="email"
              as={Input}
              hasError={touched.email && errors.email}
              helperText={touched.email && errors.email}
              label={t('forms.signup.email', 'Enter your email')}
            />
            <TwoFieldsWrapper>
              <FormikField
                name="firstName"
                as={Input}
                hasError={touched.firstName && errors.firstName}
                helperText={touched.firstName && errors.firstName}
                label={t('forms.signup.firstName', 'First Name')}
              />
              <FormikField
                name="lastName"
                as={Input}
                hasError={touched.lastName && errors.lastName}
                helperText={touched.lastName && errors.lastName}
                label={t('forms.signup.lastName', 'Last Name')}
              />
            </TwoFieldsWrapper>
            <FormikField
              name="name"
              as={Input}
              hasError={touched.name && errors.name}
              helperText={touched.name && errors.name}
              label={t('common.organizationname', 'Organization name')}
            />
            <FormikField
              name="password"
              as={Input}
              type="password"
              hasError={touched.password && errors.password}
              helperText={touched.password && errors.password}
              label={t('forms.signup.password', 'Set your password')}
            />
            <FormikField
              name="passwordRepeat"
              as={Input}
              type="password"
              hasError={touched.passwordRepeat && errors.passwordRepeat}
              helperText={touched.passwordRepeat && errors.passwordRepeat}
              label={t('forms.signup.repassword', 'Repeat Password')}
            />
            {nonFieldErrors && <ErrorMessages errors={nonFieldErrors} />}
            <CheckboxWrapper>
              <FormikField
                name="terms_agreement"
                checked={values.terms_agreement}
                onChange={() => {
                  setFieldValue('terms_agreement', !values.terms_agreement);
                }}
                as={Checkbox}
                hasError={touched.terms_agreement && errors.terms_agreement}
                label={
                  <Typography variant="copy1">
                    {t('forms.signin.agreements', 'I agree to deeptrue')}{' '}
                    <a
                      href={`https://deeptrue.com/${
                        parsedSearch?.lang || defaultLanguage
                      }/terms-and-conditions`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <TextLink>
                        {t('forms.signin.agreements2', 'Terms & Conditions')}
                      </TextLink>
                    </a>
                    {t('forms.signin.agreements5', '.')}
                  </Typography>
                }
              />
            </CheckboxWrapper>
            <CheckboxWrapper>
              <FormikField
                name="privacy_policy_accepted"
                checked={values.terms_agreement}
                onChange={() => {
                  setFieldValue(
                    'privacy_policy_accepted',
                    !values.privacy_policy_accepted
                  );
                }}
                as={Checkbox}
                hasError={
                  touched.privacy_policy_accepted &&
                  errors.privacy_policy_accepted
                }
                label={
                  <Typography variant="copy1">
                    {t('forms.signin.agreements1', 'I agree to deeptrue ')}{' '}
                    <a
                      href={`https://deeptrue.com/${
                        parsedSearch?.lang || defaultLanguage
                      }/privacy-policy`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <TextLink>
                        {t('forms.signin.agreements4', 'Privacy Policy')}
                      </TextLink>
                    </a>
                    {t('forms.signin.agreements5', '.')}
                  </Typography>
                }
              />
            </CheckboxWrapper>

            <Button
              mt="2rem"
              width="100%"
              height="3rem"
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {t('forms.signup.signupbutton', 'Get started')}
            </Button>
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export default SignUpForm;
